import { useEffect, useState } from "react";
import { ArrowClockwise, ArrowRight, Plus, X } from "@phosphor-icons/react";
import api, { fmtErr, STATUS_COLORS } from "../../lib/api";

export const StatusBadge = ({ status }) => (
  <span
    data-testid={`status-badge-${status}`}
    className={`inline-block rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${STATUS_COLORS[status] || "bg-gray-100 text-gray-700"}`}
  >
    {(status || "new").replace(/_/g, " ")}
  </span>
);

export const LeadsTable = ({ onSelect, reloadKey = 0 }) => {
  const [leads, setLeads] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newLead, setNewLead] = useState({ business_name: "", contact_name: "", email: "", listing_url: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    api.get("/admin/leads").then((r) => setLeads(r.data.leads)).catch(() => setLeads([]));
  };

  useEffect(load, []);

  const addLead = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post("/admin/leads", newLead);
      setAdding(false);
      setNewLead({ business_name: "", contact_name: "", email: "", listing_url: "" });
      load();
    } catch (e2) {
      setErr(fmtErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="leads-table">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-ink">
          Incoming Leads
        </h2>
        <div className="flex gap-2">
          <button
            data-testid="add-lead-button"
            onClick={() => setAdding(!adding)}
            className="flex items-center gap-1.5 rounded-full bg-pine px-4 py-2 text-sm font-bold text-cream transition-colors hover:bg-pine-hover"
          >
            {adding ? <X size={15} weight="bold" /> : <Plus size={15} weight="bold" />}
            {adding ? "Cancel" : "Add lead manually"}
          </button>
          <button
            data-testid="leads-refresh-button"
            onClick={load}
            className="flex items-center gap-1.5 rounded-full border border-ink/10 px-4 py-2 text-sm font-semibold text-soot transition-colors hover:border-pine hover:text-pine"
          >
            <ArrowClockwise size={15} weight="bold" />
            Refresh
          </button>
        </div>
      </div>

      {adding && (
        <form onSubmit={addLead} data-testid="add-lead-form" className="card-surface mb-6 grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <input required data-testid="add-lead-business" value={newLead.business_name} onChange={(e) => setNewLead({ ...newLead, business_name: e.target.value })} placeholder="Business name *" className="rounded-xl border border-ink/10 px-3 py-2.5 text-sm outline-none focus:border-pine" />
          <input data-testid="add-lead-contact" value={newLead.contact_name} onChange={(e) => setNewLead({ ...newLead, contact_name: e.target.value })} placeholder="Contact name" className="rounded-xl border border-ink/10 px-3 py-2.5 text-sm outline-none focus:border-pine" />
          <input data-testid="add-lead-email" value={newLead.email} onChange={(e) => setNewLead({ ...newLead, email: e.target.value })} placeholder="Email" className="rounded-xl border border-ink/10 px-3 py-2.5 text-sm outline-none focus:border-pine" />
          <input data-testid="add-lead-url" value={newLead.listing_url} onChange={(e) => setNewLead({ ...newLead, listing_url: e.target.value })} placeholder="Google Maps listing URL" className="rounded-xl border border-ink/10 px-3 py-2.5 text-sm outline-none focus:border-pine" />
          {err && <p role="alert" className="text-sm font-medium text-red-600 sm:col-span-2">{err}</p>}
          <button type="submit" disabled={busy} data-testid="add-lead-submit" className="btn-primary !py-2.5 text-sm disabled:opacity-50 sm:col-span-2">
            {busy ? "Adding…" : "Add lead"}
          </button>
        </form>
      )}

      {leads === null ? (
        <p className="text-sm text-soot">Loading…</p>
      ) : leads.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <p className="text-soot">No leads yet. New audit requests will appear here.</p>
        </div>
      ) : (
        <div className="card-surface overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-pine-soft bg-pine-light/40 text-xs uppercase tracking-wider text-soot">
                  <th className="px-5 py-3.5 font-bold">Business</th>
                  <th className="px-5 py-3.5 font-bold">Contact</th>
                  <th className="hidden px-5 py-3.5 font-bold md:table-cell">Email</th>
                  <th className="px-5 py-3.5 font-bold">Status</th>
                  <th className="hidden px-5 py-3.5 font-bold sm:table-cell">Received</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr
                    key={l.id}
                    data-testid={`lead-row-${l.id}`}
                    onClick={() => onSelect(l.id)}
                    className="cursor-pointer border-b border-pine-soft/60 transition-colors last:border-0 hover:bg-pine-light/30"
                  >
                    <td className="px-5 py-4 font-semibold text-ink">{l.business_name}</td>
                    <td className="px-5 py-4 text-soot">{l.contact_name || "—"}</td>
                    <td className="hidden px-5 py-4 text-soot md:table-cell">{l.email || "—"}</td>
                    <td className="px-5 py-4"><StatusBadge status={l.status} /></td>
                    <td className="hidden px-5 py-4 text-soot sm:table-cell">
                      {new Date(l.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-5 py-4 text-pine">
                      <ArrowRight size={16} weight="bold" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
